import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import './_styles_/RequestRateLimitCreate.css';
import SelectField from '../fields/SelectField';
import { getRequestType } from '../../utils/RequestTypes';
import NumberField from '../fields/NumberField';
import {
    requestRateLimitPropertiesType,
    requestRateLimitsType,
} from '../../types';
import useExtendedState from '../../hooks/useExtendedState';
import Constants from '../../utils/Constants';
import useSingleValidator from '../../hooks/useSingleValidator';
import { settingsRateLimitValidator } from '../../validators/settings';
import { formatUserForDisplay } from './RequestRateLimitsTableRow';
import { createTemporaryId } from '../../utils/tableRecords';

export const usersSortFunc = (t) => (firstUser, secondUser) => {
    const firstUserForDisplay = formatUserForDisplay(t, firstUser);
    const secondUserForDisplay = formatUserForDisplay(t, secondUser);
    return firstUserForDisplay.localeCompare(secondUserForDisplay);
};

RequestRateLimitCreate.propTypes = {
    requestRateLimits: requestRateLimitsType.isRequired,
    requestRateLimitProps: requestRateLimitPropertiesType.isRequired,
    onRequestRateLimitCreate: PropTypes.func.isRequired,
};

function RequestRateLimitCreate(props) {
    const {
        requestRateLimits,
        requestRateLimitProps,
        onRequestRateLimitCreate,
    } = props;
    const {
        availableUsers,
    } = requestRateLimitProps;
    const { t } = useTranslation();
    const sortedAvailableUsers = [...availableUsers].sort(usersSortFunc(t));
    const userIdToAddState = useExtendedState('');
    const requestTypeToAddState = useExtendedState('');
    const rateLimitToAddState = useExtendedState(1);
    const userToAddState = useExtendedState(null);
    const setNewUserIdToAdd = (newUserIdToAdd) => {
        userToAddState.setValue(newUserIdToAdd ? sortedAvailableUsers.find((availableUser) => availableUser.racf === newUserIdToAdd) : null);
    };
    const { validator, isValid } = useSingleValidator(true, useMemo(() => settingsRateLimitValidator(t, Constants.MIN_RATE_LIMIT_VALUE, Constants.MAX_RATE_LIMIT_VALUE), [t]));
    const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(true);
    const initialValidation = !requestTypeToAddState.value || !userIdToAddState.value || !rateLimitToAddState.value ? Promise.reject() : Promise.resolve();
    Promise.all([initialValidation, isValid()])
        .then(() => setIsAddButtonDisabled(false))
        .catch(() => setIsAddButtonDisabled(true));

    const addRequestRateLimit = () => {
        onRequestRateLimitCreate(createTemporaryId(), userIdToAddState.value === Constants.ALL_USERS_OPTION_VALUE ? null : userToAddState.value, requestTypeToAddState.value, rateLimitToAddState.value);
        requestTypeToAddState.reset();
        userIdToAddState.reset();
        rateLimitToAddState.reset();
        userIdToAddState.reset();
    };
    const getRemainingRequestTypesForUser = (limitsForUser) => requestRateLimitProps.availableRequestTypes.filter((requestType) => {
        const existsLimitForRequestType = limitsForUser.find((limitForUser) => limitForUser.requestType === requestType);
        return !existsLimitForRequestType;
    });
    const usableUsers = sortedAvailableUsers.filter((availableUser) => {
        const limitsForUser = requestRateLimits.filter((requestRateLimit) => (requestRateLimit.user ? requestRateLimit.user.racf === availableUser.racf : false));
        return getRemainingRequestTypesForUser(limitsForUser).length > 0;
    });
    const isAvailableAllUsersOption = getRemainingRequestTypesForUser(requestRateLimits.filter((requestRateLimit) => (requestRateLimit.user === null))).length > 0;
    const userOptions = (
        <>
            {isAvailableAllUsersOption && (
                <option
                    value={Constants.ALL_USERS_OPTION_VALUE}
                >
                    {t('settings.request-rates-and-limits.request-rate-limits.all-users')}
                </option>
            )}
            {usableUsers.map((usableUser) => (
                <option
                    key={usableUser.racf}
                    value={usableUser.racf}
                >
                    {formatUserForDisplay(t, usableUser)}
                </option>
            ))}
        </>
    );
    const requestTypeOptions = getRemainingRequestTypesForUser(
        requestRateLimits.filter((requestRateLimit) => (
            !userIdToAddState.value || userIdToAddState.value === Constants.ALL_USERS_OPTION_VALUE
                ? requestRateLimit.user === null
                : (requestRateLimit.user && requestRateLimit.user.racf === userToAddState.value.racf)
        )),
    ).map((availableRequestType) => (
        <option
            key={availableRequestType}
            value={availableRequestType}
        >
            {getRequestType(availableRequestType).translate(t)}
        </option>
    ));
    return (
        <div className="request-rate-limits--add-request-rate-limit">
            <SelectField
                label={t('settings.request-rates-and-limits.request-rate-limits.user')}
                inputId="request-rate-limits--fields--user"
                className="request-rate-limits--fields--user"
                onChange={setNewUserIdToAdd}
                emptyOption={<option value="">...</option>}
                state={userIdToAddState}
                options={userOptions}
            />
            <SelectField
                label={t('settings.request-rates-and-limits.request-rate-limits.request-type')}
                inputId="request-rate-limits--fields--request-type"
                className="request-rate-limits--fields--request-type"
                emptyOption={<option value="">...</option>}
                state={requestTypeToAddState}
                options={requestTypeOptions}
            />
            <NumberField
                label={t('settings.request-rates-and-limits.request-rate-limits.rate-limit')}
                inputId="request-rate-limits--fields--rate-limit"
                className="request-rate-limits--fields--rate-limit"
                state={rateLimitToAddState}
                step={Constants.RATE_LIMIT_VALUE_STEP}
                min={Constants.MIN_RATE_LIMIT_VALUE}
                max={Constants.MAX_RATE_LIMIT_VALUE}
                decrementDisabled={rateLimitToAddState.value - Constants.RATE_LIMIT_VALUE_STEP < Constants.MIN_RATE_LIMIT_VALUE}
                incrementDisabled={rateLimitToAddState.value + Constants.RATE_LIMIT_VALUE_STEP > Constants.MAX_RATE_LIMIT_VALUE}
                validator={validator}
            />
            <button
                type="button"
                className="add-request-rate-limit-button button button-bordered"
                disabled={isAddButtonDisabled}
                onClick={addRequestRateLimit}
            >
                <FontAwesomeIcon icon="plus" />&nbsp;{t('settings.request-rates-and-limits.request-rate-limits.add-request-rate-limit')}
            </button>
        </div>
    );
}

export default RequestRateLimitCreate;
